import { Amount, BondPriceNominal, CouponPayment, MoodysRating, SpRating, Yield } from "@goodfill/domain-properties";
import { CorporateBondId, IssuerId } from "@goodfill/domain-properties";
import { PresentedAccountBondHolding } from "./PresentedAccountBondHolding";
import { DateTime } from "luxon";
import {
  maturityDateFilter,
  MATURITY_CRITERIA_KEY,
} from "../../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { FilterFunction } from "../../../../../filters/filters";
import {
  MOODYS_RATING_CRITERIA_KEY,
  moodysRatingFilter,
} from "../../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  PAYMENT_RANK_CRITERIA_KEY,
  paymentRankFilter,
} from "../../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import {
  SP_RATING_CRITERIA_KEY,
  spRatingFilter,
} from "../../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import { Range } from "../../../../DataGridFilteredColumns/generic/RangeColumnDefinition";
import {
  COUPON_PAYMENT_CRITERIA_KEY,
  couponPaymentFilter,
} from "../../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";
import { ISIN_CRITERIA_KEY, isinFilter } from "../../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  ISSUER_ID_CRITERIA_KEY,
  issuerIdFilter,
} from "../../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { TICKER_CRITERIA_KEY, tickersFilter } from "../../../../DataGridFilteredColumns/bond/TickerColumnDefinition";

export interface PortfolioHoldingsFilter {
  [ISIN_CRITERIA_KEY]: CorporateBondId[];
  [ISSUER_ID_CRITERIA_KEY]: IssuerId[];
  [TICKER_CRITERIA_KEY]: string[];
  [MATURITY_CRITERIA_KEY]: [DateTime | null, DateTime | null];
  balanceRange: [Amount | null, Amount | null];
  baseBondBidPriceRange: [BondPriceNominal | null, BondPriceNominal | null];
  baseBondBidYieldRange: [Yield | null, Yield | null];
  bidBondValueRange: [Amount | null, Amount | null];
  [MOODYS_RATING_CRITERIA_KEY]: Range<MoodysRating>;
  [SP_RATING_CRITERIA_KEY]: Range<SpRating>;
  [PAYMENT_RANK_CRITERIA_KEY]: string[];
  [COUPON_PAYMENT_CRITERIA_KEY]: CouponPayment[];
}

export const defaultPortfolioHoldingsFilterConstraints: PortfolioHoldingsFilter = {
  [ISIN_CRITERIA_KEY]: [],
  [ISSUER_ID_CRITERIA_KEY]: [],
  [TICKER_CRITERIA_KEY]: [],
  [MATURITY_CRITERIA_KEY]: [null, null],
  balanceRange: [null, null],
  baseBondBidPriceRange: [null, null],
  baseBondBidYieldRange: [null, null],
  bidBondValueRange: [null, null],
  [MOODYS_RATING_CRITERIA_KEY]: [null, null],
  [SP_RATING_CRITERIA_KEY]: [null, null],
  [PAYMENT_RANK_CRITERIA_KEY]: [],
  [COUPON_PAYMENT_CRITERIA_KEY]: [],
};

export const portfolioHoldingsFilters: FilterFunction<PresentedAccountBondHolding, PortfolioHoldingsFilter>[] = [
  isinFilter("bondId"),
  issuerIdFilter("issuerId"),
  tickersFilter("ticker"),
  paymentRankFilter("paymentRank"),
  couponPaymentFilter("couponPayment"),
  maturityDateFilter("maturityDate"),

  // Balance
  (item, constrains) =>
    constrains.balanceRange[0] === null || constrains.balanceRange[0].lessThanOrEqualTo(item.balance),
  (item, constrains) =>
    constrains.balanceRange[1] === null || constrains.balanceRange[1].greaterThanOrEqualTo(item.balance),

  // Bid Last Price
  (item, constrains) =>
    constrains.baseBondBidPriceRange[0] === null ||
    constrains.baseBondBidPriceRange[0].lessThanOrEqualTo(item.baseBondBidPrice),
  (item, constrains) =>
    constrains.baseBondBidPriceRange[1] === null ||
    constrains.baseBondBidPriceRange[1].greaterThanOrEqualTo(item.baseBondBidPrice),

  // Yield
  (item, constrains) =>
    constrains.baseBondBidYieldRange[0] === null ||
    constrains.baseBondBidYieldRange[0].lessThanOrEqualTo(item.baseBondBidYield),
  (item, constrains) =>
    constrains.baseBondBidYieldRange[1] === null ||
    constrains.baseBondBidYieldRange[1].greaterThanOrEqualTo(item.baseBondBidYield),

  // Value
  (item, constrains) =>
    constrains.bidBondValueRange[0] === null || constrains.bidBondValueRange[0].lessThanOrEqualTo(item.bidBondValue),
  (item, constrains) =>
    constrains.bidBondValueRange[1] === null ||
    constrains.bidBondValueRange[1].greaterThanOrEqualTo(item.bidBondValue),

  // Rating
  spRatingFilter("bondRatingSp"),
  moodysRatingFilter("bondRatingMoodys"),
];
